import type { WeightedItem } from '@/utils/weighted-pick';

/**
 * Builds a play order where each item appears proportional to its weight and
 * high-weight items are spread evenly before lower-weight items fill the gaps.
 *
 * Example — weights A:5, B:3, C:2 (deck size 10):
 * 1. A → [a,_,a,_,a,_,a,_,a,_]
 * 2. B → [a,b,a,_,a,b,a,_,a,b]
 * 3. C → [a,b,a,c,a,b,a,c,a,b]
 */
export function buildWeightedDeck<T extends WeightedItem>(items: T[]): T[] {
  const playable = items.filter((item) => item.weight > 0);
  if (!playable.length) return [];
  if (playable.length === 1) return [playable[0]];

  const deckSize = playable.reduce((sum, item) => sum + item.weight, 0);
  if (deckSize <= 0) return [];

  const deck: (T | null)[] = Array.from({ length: deckSize }, () => null);
  const sorted = [...playable].sort(
    (a, b) => b.weight - a.weight || a.mediaId.localeCompare(b.mediaId),
  );

  for (const item of sorted) {
    const emptyIndices: number[] = [];
    for (let index = 0; index < deck.length; index += 1) {
      if (deck[index] === null) {
        emptyIndices.push(index);
      }
    }

    const count = item.weight;
    if (!emptyIndices.length || count <= 0) continue;

    for (let placement = 0; placement < count; placement += 1) {
      const slot = pickEvenlySpacedSlot(placement, count, emptyIndices.length, deck.length);
      deck[emptyIndices[slot]] = item;
    }
  }

  return deck.filter((item): item is T => item !== null);
}

/** Picks a slot index within `emptyCount` gaps. */
function pickEvenlySpacedSlot(
  placement: number,
  count: number,
  emptyCount: number,
  deckSize: number,
): number {
  if (count === 1) {
    return Math.floor((emptyCount - 1) / 2);
  }

  if (emptyCount === deckSize) {
    return Math.min(emptyCount - 1, Math.floor((placement * emptyCount) / count));
  }

  return Math.min(emptyCount - 1, Math.round((placement * (emptyCount - 1)) / (count - 1)));
}

export function findNextInDeck<T extends WeightedItem>(
  deck: T[],
  startIndex: number,
  excludeMediaIds: string[] = [],
): { item: T; index: number } | null {
  if (!deck.length) return null;

  const excludeSet = new Set(excludeMediaIds);

  for (let offset = 0; offset < deck.length; offset += 1) {
    const index = (startIndex + offset) % deck.length;
    const item = deck[index];
    if (!excludeSet.has(item.mediaId)) {
      return { item, index };
    }
  }

  return null;
}
